import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Progress } from '../api';

export function useProgress(enabled: boolean) {
  return useQuery<Progress>({
    queryKey: ['progress'],
    queryFn: api.progress,
    enabled,
    staleTime: 60_000,
  });
}

export function useToggleComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, completed }: { slug: string; completed: boolean }) =>
      completed ? api.markComplete(slug) : api.unmarkComplete(slug),
    onMutate: async ({ slug, completed }) => {
      await qc.cancelQueries({ queryKey: ['progress'] });
      const prev = qc.getQueryData<Progress>(['progress']);
      if (prev) {
        const set = new Set(prev.completed);
        if (completed) set.add(slug); else set.delete(slug);
        qc.setQueryData<Progress>(['progress'], { ...prev, completed: Array.from(set) });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['progress'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['progress'] }),
  });
}

export function useSaveJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, promptKey, content }: { slug: string; promptKey: string; content: string }) =>
      api.saveJournal(slug, promptKey, content),
    onMutate: async ({ slug, promptKey, content }) => {
      await qc.cancelQueries({ queryKey: ['progress'] });
      const prev = qc.getQueryData<Progress>(['progress']);
      if (prev) {
        const nextJournal = { ...prev.journal, [slug]: { ...(prev.journal[slug] ?? {}), [promptKey]: content } };
        qc.setQueryData<Progress>(['progress'], { ...prev, journal: nextJournal });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['progress'], ctx.prev);
    },
  });
}
